import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Check, Download, Loader2, RotateCcw, Trash2, Upload } from "lucide-react";

import { TableActionsMenu, type TableAction } from "@/components/shared/TableActionsMenu";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { formatDisplayDate } from "@/lib/dateFormat";
import { formatDashboardCurrency } from "@/lib/currencyFormat";
import {
  getAuthorityLabelKey,
  getTaxLabelKey,
  getStatusBadgeVariant,
} from "@/lib/fiscalAgendaDisplay";
import { cn } from "@/lib/utils";
import type { FiscalAgendaObligation } from "@/services/fiscalService";
import { isDeclareOnly, saldoAFavorOf } from "@/pages/fiscal-agenda/declareOnly";

export type FiscalAgendaPaymentDocumentSectionProps = {
  obligation: FiscalAgendaObligation;
  agendaTab: "pendientes" | "completados";
  isAccountant: boolean;
  supportsPaymentDocument: boolean;
  isInCurrentMonth: boolean;
  onRequestBoleto: () => void;
  isRequestingBoleto: boolean;
  onOpenUpload: () => void;
  onOpenRemove: () => void;
  onOpenMarkComplete?: () => void;
};

export function FiscalAgendaPaymentDocumentSection({
  obligation,
  agendaTab,
  isAccountant,
  supportsPaymentDocument,
  isInCurrentMonth,
  onRequestBoleto,
  isRequestingBoleto,
  onOpenUpload,
  onOpenRemove,
  onOpenMarkComplete,
}: FiscalAgendaPaymentDocumentSectionProps): ReactNode {
  const { t } = useTranslation();
  const hasBoleto = Boolean(obligation.boletoFileUrl && obligation.boletoUploadedAt);

  if (agendaTab === "completados") {
    if (hasBoleto) {
      return (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => window.open(obligation.boletoFileUrl ?? "", "_blank")}
          className="gap-1.5 transition-colors duration-150"
        >
          <Download className="h-4 w-4" />
          {t("fiscalAgenda.downloadDocument")}
        </Button>
      );
    }
    return "—";
  }
  if (!supportsPaymentDocument) return "—";
  if (isAccountant && isInCurrentMonth) {
    if (hasBoleto) {
      const actions: TableAction[] = [
        {
          label: t("fiscalAgenda.downloadDocument"),
          icon: Download,
          onClick: () => window.open(obligation.boletoFileUrl ?? "", "_blank"),
        },
        {
          label: t("fiscalAgenda.replaceDocument"),
          icon: Upload,
          onClick: onOpenUpload,
        },
        {
          label: t("fiscalAgenda.removeDocument"),
          icon: Trash2,
          onClick: onOpenRemove,
          variant: "destructive",
        },
      ];
      return (
        <TableActionsMenu
          actions={actions}
          onTriggerClick={(e) => e.stopPropagation()}
          triggerLabel={t("fiscalAgenda.actions")}
        />
      );
    }
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onOpenUpload}
        className="gap-1.5 transition-colors duration-150"
      >
        <Upload className="h-4 w-4" />
        {t("fiscalAgenda.uploadDocument")}
      </Button>
    );
  }
  if (!isAccountant) {
    if (hasBoleto) {
      const tenantActions: TableAction[] = [
        {
          label: t("fiscalAgenda.downloadDocument"),
          icon: Download,
          onClick: () => window.open(obligation.boletoFileUrl ?? "", "_blank"),
        },
        ...(onOpenMarkComplete
          ? [
              {
                label: t("fiscalAgenda.markCompleted"),
                icon: Check,
                onClick: onOpenMarkComplete,
              } as TableAction,
            ]
          : []),
      ];
      return (
        <TableActionsMenu
          actions={tenantActions}
          onTriggerClick={(e) => e.stopPropagation()}
          triggerLabel={t("fiscalAgenda.actions")}
        />
      );
    }
    if (obligation.boletoRequestedAt) {
      return (
        <Badge variant="secondary" className="inline-flex w-fit text-xs font-medium">
          {t("fiscalAgenda.requested")}
        </Badge>
      );
    }
    if (!isInCurrentMonth) return "—";
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onRequestBoleto()}
        disabled={isRequestingBoleto}
        className="gap-1.5 transition-colors duration-150"
      >
        {isRequestingBoleto ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {t("fiscalAgenda.requestDocument")}
      </Button>
    );
  }
  return "—";
}

export type FiscalAgendaMobileCardProps = {
  obligation: FiscalAgendaObligation;
  periodLabel: string;
  locale?: string;
  showNotes: boolean;
  agendaTab: "pendientes" | "completados";
  isAccountant: boolean;
  supportsPaymentDocument: boolean;
  isInCurrentMonth: boolean;
  onRequestBoleto: () => void;
  isRequestingBoleto: boolean;
  onOpenUpload: () => void;
  onOpenRemove: () => void;
  onOpenMarkComplete?: () => void;
  onUncomplete?: () => void;
  isUncompleting?: boolean;
};

export function FiscalAgendaMobileCard({
  obligation,
  periodLabel,
  locale,
  showNotes,
  agendaTab,
  isAccountant,
  supportsPaymentDocument,
  isInCurrentMonth,
  onRequestBoleto,
  isRequestingBoleto,
  onOpenUpload,
  onOpenRemove,
  onOpenMarkComplete,
  onUncomplete,
  isUncompleting,
}: FiscalAgendaMobileCardProps): JSX.Element {
  const { t } = useTranslation();
  const statusKey = obligation.status;
  // Nothing to pay: the declaración is still due, so the row stays — but it
  // must not be dressed as an unpaid debt (see declareOnly.ts).
  const declareOnly = isDeclareOnly(obligation);
  const saldoAFavor = saldoAFavorOf(obligation);
  const isDueToday = obligation.status === "due_today" && !declareOnly;
  const isOverdue = obligation.status === "overdue" && !declareOnly;

  return (
    <Card
      className={cn(
        "space-y-3 overflow-hidden border-border p-4 text-left",
        isOverdue && "border-l-4 border-l-red-500 bg-red-50 dark:bg-red-950/30",
        isDueToday &&
          "border-l-4 border-l-amber-500 bg-amber-50 dark:bg-amber-950/30"
      )}
    >
      <div className="grid gap-1 text-sm">
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">{t("fiscalAgenda.columnAuthority")}</span>
          <span className="font-medium text-foreground text-right">
            {t(getAuthorityLabelKey(obligation.authority))}
          </span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">{t("fiscalAgenda.columnTax")}</span>
          <span className="font-medium text-foreground text-right">
            {t(getTaxLabelKey(obligation.taxType))}
          </span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">{t("fiscalAgenda.columnPeriod")}</span>
          <span className="text-right text-foreground">{periodLabel}</span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">{t("fiscalAgenda.columnDueDate")}</span>
          <span className="text-right text-foreground">
            {formatDisplayDate(obligation.dueDate, {
              useCalendarDate: true,
              locale,
            })}
            {obligation.internetPaymentDueDate ? (
              <span className="mt-0.5 block text-xs text-muted-foreground">
                {t("fiscalAgenda.internetPaymentUntil", "Por internet, hasta el {{date}}", {
                  date: formatDisplayDate(obligation.internetPaymentDueDate, {
                    useCalendarDate: true,
                    locale,
                  }),
                })}
              </span>
            ) : null}
          </span>
        </div>
      </div>
      <div>
        <StatusBadge
          variant={declareOnly ? "neutral" : getStatusBadgeVariant(obligation.status)}
          className={cn(
            "inline-flex text-xs font-medium",
            (isDueToday || isOverdue) && "font-semibold"
          )}
        >
          {declareOnly
            ? t("fiscalAgenda.status.declareOnly", "A declarar — sin importe a pagar")
            : t(`fiscalAgenda.status.${statusKey}`)}
        </StatusBadge>
        {declareOnly && saldoAFavor > 0 ? (
          <p className="mt-1 text-xs text-muted-foreground">
            {t("fiscalAgenda.saldoAFavor", "Saldo a favor")}:{" "}
            $U {formatDashboardCurrency(saldoAFavor)}
          </p>
        ) : null}
      </div>
      <div className="border-t border-border pt-3">
        <p className="mb-2 text-xs font-medium text-muted-foreground">
          {t("fiscalAgenda.columnPaymentDocument")}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <FiscalAgendaPaymentDocumentSection
            obligation={obligation}
            agendaTab={agendaTab}
            isAccountant={isAccountant}
            supportsPaymentDocument={supportsPaymentDocument}
            isInCurrentMonth={isInCurrentMonth}
            onRequestBoleto={onRequestBoleto}
            isRequestingBoleto={isRequestingBoleto}
            onOpenUpload={onOpenUpload}
            onOpenRemove={onOpenRemove}
            onOpenMarkComplete={onOpenMarkComplete}
          />
        </div>
      </div>
      {showNotes && (
        <div className="space-y-2 border-t border-border pt-3 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">
              {t("fiscalAgenda.columnPaymentDate")}
            </span>
            <span className="text-right">
              {obligation.completedAt
                ? formatDisplayDate(obligation.completedAt, {
                    useCalendarDate: true,
                    locale,
                  })
                : "—"}
            </span>
          </div>
          <p className="text-muted-foreground">
            <span className="font-medium text-foreground">
              {t("fiscalAgenda.columnNotes")}:{" "}
            </span>
            {obligation.notes ?? "—"}
          </p>
          {onUncomplete ? (
            <TableActionsMenu
              actions={[
                {
                  label: t("fiscalAgenda.uncomplete"),
                  icon: RotateCcw,
                  onClick: onUncomplete,
                },
              ]}
              triggerLabel={t("fiscalAgenda.actions")}
              disabled={isUncompleting}
            />
          ) : null}
        </div>
      )}
    </Card>
  );
}
